/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, InputGroup, Modal, Toast } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import LoadingCircle from '../../LoadingCircle';
import DesktopOnlyMessage from '../../DesktopOnlyMessage/DesktopOnlyMessage';
import vcService from '../../../services/vcService';
import './CreateVC.css';
import successImage from '../../../assets/images/success.png';
import { isMobile, isTablet } from '../../../services/device/deviceService';
import usePageVisibility from '../../../hooks/usePageVisibility';

const QR_VALIDITY_MS = 2 * 60 * 1000; // 2 minutes

export const CreateVC = ({ 
  vidMobileUserGuid, 
  certificateGuid, 
  certificateType,
  trigger,
  onCredentialCreated,
  credentialNamespace
}) => {
  const { t } = useTranslation();
  const [certificatePin, setCertificatePin] = useState('');
  const [loadingQr, setLoadingQr] = useState(false);
  const [qrList, setQrList] = useState([]);
  const [qrExpired, setQrExpired] = useState(false);
  const [error, setError] = useState(null);
  const [showModalPin, setShowModalPin] = useState(false);
  const [showDesktopOnlyModal, setShowDesktopOnlyModal] = useState(false);
  const [showModalQR, setShowModalQR] = useState(false);
  const [showMultipleQrModal, setShowMultipleQrModal] = useState(false);
  const [success, setSuccess] = useState(false);
  const [originalQrAmount, setOriginalQrAmount] = useState(0);
  const [currentRequestId, setCurrentRequestId] = useState(null);
  const [processedQrCount, setProcessedQrCount] = useState(0);
  const qrTimeoutRef = React.useRef(null);
  // Mobile-specific: confirmation modal shown after first QR is socket-confirmed
  const [showMobileNextCredentialModal, setShowMobileNextCredentialModal] = useState(false);
  const [pendingDeeplinkUrl, setPendingDeeplinkUrl] = useState(null);

  const isMobileDevice = useMemo(() => isMobile() || isTablet(), []);

  /**
   * Core handler for a successfully issued VC.
   * Extracted so it can be called both by the WebSocket event handler and by the
   * REST poll fallback (handleWindowFocusAfterDeeplinkRedirect).
   */
  const processVCIssued = useCallback((data) => {
    const { issuanceId } = data;

    if (issuanceId && vidMobileUserGuid) {
      vcService.updateCredentialStatus(vidMobileUserGuid, issuanceId, 'accepted')
        .catch((error) => {
          console.error('Error updating credential status:', error);
          setError(error.response?.data?.message || error.message || 'Error al actualizar el estado de la credencial');
          setShowModalQR(false);
          setSuccess(false);
        });
    }

    const storedQrList = JSON.parse(localStorage.getItem('QrInfoList')) || [];
    const totalQrCount = originalQrAmount || storedQrList.length;

    setProcessedQrCount(prev => {
      const newCount = prev + 1;

      if (newCount < totalQrCount) {
        if (isMobileDevice) {
          const nextDeeplinkUrl = storedQrList[newCount]?.Deeplink;
          setPendingDeeplinkUrl(nextDeeplinkUrl || null);
          setShowMobileNextCredentialModal(true);
        } else {
          if (newCount < storedQrList.length) {
            setQrList([storedQrList[newCount]]);
          }
        }
        return newCount;
      }

      setShowModalQR(false);
      setSuccess(true);
      return newCount;
    });
  }, [vidMobileUserGuid, originalQrAmount, isMobileDevice]);

  /**
   * Core handler for a rejected VC.
   * Extracted for the same reason as processVCIssued.
   */
  const processVCRejected = useCallback((data) => {
    const { issuanceId } = data;

    if (issuanceId && vidMobileUserGuid) {
      vcService.updateCredentialStatus(vidMobileUserGuid, issuanceId, 'Rejected')
        .catch((error) => {
          console.error('Error updating credential status:', error);
        });
    }

    setError(t('documents.error-rejected-signature') || 'Credential issuance was rejected');
    setShowModalQR(false);
  }, [vidMobileUserGuid, t]);

  /**
   * Called when the page regains visibility after a mobile deeplink redirect.
   * Polls the backend for the stored operation result in case the WebSocket
   * disconnected while the browser was in the background.
   * The backend deletes the entry after returning it (one-time read).
   */
  const handleWindowFocusAfterDeeplinkRedirect = async () => {
    if (!currentRequestId) return;

    try {
      const response = await vcService.getOperationByRequestId(currentRequestId);
      const operation = response?.data;

      if (!operation || !operation.status) return;

      if (operation.status === 'issued') {
        processVCIssued(operation);
      } else if (operation.status === 'rejected') {
        processVCRejected(operation);
      }
    } catch (error) {
      console.error('Error fetching operation by requestId:', error);
    }
  };

  const hideModalPin = () => {
    setShowModalPin(false);
    setCertificatePin('');
    setError(null);
  };

  const requestQR = () => {
    // Prevent issuing VCs from mobile devices: show desktop-only message
    if (isMobileDevice) {
      setShowDesktopOnlyModal(true);
      return;
    }
    if (qrTimeoutRef.current) {
      clearTimeout(qrTimeoutRef.current);
      qrTimeoutRef.current = null;
    }
    setQrExpired(false);
    setLoadingQr(true);
    setQrList([]);
    setError(null);

    const body = {
      Pin: certificatePin,
      CertificateGuid: certificateGuid,
      CertificateType: certificateType,
    };

    vcService.getCredentialQR(vidMobileUserGuid, body)
      .then((response) => {
        const responseData = response.data;
        const { QrInfoList, RequestId } = responseData;

        localStorage.setItem('QrInfoList', JSON.stringify(QrInfoList));
        setProcessedQrCount(0);

        const length = QrInfoList.length;
        setOriginalQrAmount(length);
        
        if (QrInfoList.length > 0) {
          setQrList([QrInfoList[0]]);
        }

        if (RequestId) {
          setCurrentRequestId(RequestId);

          if (isMobileDevice) {
            // On mobile: save RequestId and always register socket listener.
            // No longer redirecting back via a callback URL — socket confirmation is used instead.
            localStorage.setItem('RequestId', RequestId);
            credentialNamespace.emit('listenCredential', RequestId);

            if (length > 1) {
              // Multiple QRs: show informational modal first (same as desktop), then redirect
              setShowMultipleQrModal(true);
            } else {
              // Single QR: activate socket listeners and redirect to deeplink directly
              setShowModalQR(true);
              const deeplinkUrl = QrInfoList[0]?.Deeplink;
              if (deeplinkUrl) {
                window.location.href = deeplinkUrl;
              }
            }
          } else {
            // Desktop: show QR modal / multiple-QR info modal and emit socket listener
            if (length > 1) {
              setShowMultipleQrModal(true);
            } else {
              setShowModalQR(true);
            }
            credentialNamespace.emit('listenCredential', RequestId);
          }
        }
      })
      .catch((error) => {
        setError(error.response?.data || error.message || 'Error requesting VC issuance');
        setShowModalQR(false);
        console.error('Error requesting VC issuance:', error);
      })
      .finally(() => {
        setLoadingQr(false);
      });
  };

  useEffect(() => {
    if (trigger) {
      // On mobile/tablet show the desktop-only informational modal before any PIN prompt
      if (isMobileDevice) {
        setShowDesktopOnlyModal(true);
        setError(null);
        setSuccess(false);
      } else {
        setShowModalPin(true);
        setError(null);
        setSuccess(false);
      }
    }
  }, [trigger]);

  const handleAcceptPin = () => {
    hideModalPin();
    requestQR();
  };

  const handleAcceptMultipleQr = () => {
    const storedQrList = JSON.parse(localStorage.getItem('QrInfoList')) || [];
    if (storedQrList.length > 0) {
      setQrList([storedQrList[0]]);
    }
    setQrExpired(false);
    setShowMultipleQrModal(false);

    if (isMobileDevice) {
      // Mobile: activate socket listeners and redirect to first deeplink
      setShowModalQR(true);
      const deeplinkUrl = storedQrList[0]?.Deeplink;
      if (deeplinkUrl) {
        window.location.href = deeplinkUrl;
      }
    } else {
      // Desktop: show QR image modal
      setShowModalQR(true);
    }
  };

  /**
   * Called when the user dismisses the "first credential validated" confirmation modal on mobile.
   * Navigates to the next deeplink so the user can complete the remaining credential.
   */
  const handleMobileNextCredential = () => {
    setShowMobileNextCredentialModal(false);
    if (pendingDeeplinkUrl) {
      window.location.href = pendingDeeplinkUrl;
      setPendingDeeplinkUrl(null);
    }
  };

  useEffect(() => {
    if (showModalQR) {
      // Track processed issuanceIds to avoid duplicate processing of the same socket event
      const processedIssuanceIds = new Set();

      const handleVCIssued = (data) => {
        const { issuanceId } = data;

        // avoid processing the same issuanceId twice
        if (processedIssuanceIds.has(issuanceId)) return;
        processedIssuanceIds.add(issuanceId);

        processVCIssued(data);
      };

      const handleVCRejected = (data) => {
        const { issuanceId } = data;

        if (processedIssuanceIds.has(issuanceId)) return;
        processedIssuanceIds.add(issuanceId);

        processVCRejected(data);
      };

      credentialNamespace.on('vc:issued', handleVCIssued);
      credentialNamespace.on('vc:rejected', handleVCRejected);

      return () => {
        credentialNamespace.off('vc:issued', handleVCIssued);
        credentialNamespace.off('vc:rejected', handleVCRejected);
      };
    }
  }, [showModalQR, processVCIssued, processVCRejected]);

  const currentQr = qrList.length > 0 ? qrList[0] : null;

  useEffect(() => {
    if (!showModalQR || !currentQr) return;

    if (qrTimeoutRef.current) {
      clearTimeout(qrTimeoutRef.current);
      qrTimeoutRef.current = null;
    }

    qrTimeoutRef.current = setTimeout(() => {
      qrTimeoutRef.current = null;
      setQrExpired(true);
    }, QR_VALIDITY_MS);

    return () => {
      if (qrTimeoutRef.current) {
        clearTimeout(qrTimeoutRef.current);
        qrTimeoutRef.current = null;
      }
    };
  }, [showModalQR, currentQr]);
  const isVisible = usePageVisibility();

  useEffect(() => {
    if (isMobileDevice &&
        showModalQR &&
        isVisible) {
      handleWindowFocusAfterDeeplinkRedirect();
    } 
  }, [isVisible]); // Re-run effect when the visibility state changes

  return (
    <>
      {/* Desktop-only informational modal shown when user tries to issue VC from mobile */}
      <DesktopOnlyMessage show={showDesktopOnlyModal} onClose={() => setShowDesktopOnlyModal(false)} />

      {/* QR display modal (desktop) / wallet waiting modal (mobile) */}
      <Modal 
        show={showModalQR} 
        onHide={() => { setShowModalQR(false); setCertificatePin(''); }}
        centered 
        className="wallet-authorization-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t('certificates.wallet-authorization-title')}
            {!isMobileDevice && originalQrAmount > 1 && ` - ${processedQrCount + 1}/${originalQrAmount}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="wallet-auth-modal-body">
          {qrExpired ? (
            <p className="wallet-auth-expired-message">
              {t('certificates.qr-validation-time-expired')}
            </p>
          ) : loadingQr ? (
            <div className="wallet-auth-loading">
              <LoadingCircle />
              <p>{t('certificates.wallet-authorization-loading')}</p>
            </div>
          ) : isMobileDevice ? (
            // Mobile: show a waiting spinner while the wallet app processes the credential
            <div className="wallet-auth-loading">
              <LoadingCircle />
              <p>{t('certificates.wallet-redirecting')}</p>
            </div>
          ) : currentQr ? (
            <div className="wallet-auth-content">
              <p className="wallet-auth-instructions">
                {t('certificates.qr-credential-instructions', { type: currentQr.Type || '' })}
              </p>
              <div className="qr-code-container">
                <img src={currentQr.Data} alt="QR Code" className="qr-code-image" />
              </div>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="reject"
            onClick={() => {
              if (qrTimeoutRef.current) {
                clearTimeout(qrTimeoutRef.current);
                qrTimeoutRef.current = null;
              }
              setShowModalQR(false);
              setCertificatePin('');
              setQrExpired(false);
            }}
          >
            {t('general.cancel')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Informational modal shown when there are multiple QRs to process */}
      <Modal show={showMultipleQrModal} onHide={() => setShowMultipleQrModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{isMobileDevice ? t('certificates.multiple-qr-title-mobile') : t('certificates.multiple-qr-title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {
              isMobileDevice ?
              t('certificates.multiple-qr-description-mobile') :
              t('certificates.multiple-qr-description')
            }
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleAcceptMultipleQr}>
            {t('general.accept')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Mobile-only: confirmation modal shown after the first credential is socket-confirmed */}
      <Modal show={showMobileNextCredentialModal} onHide={handleMobileNextCredential} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('certificates.mobile-next-credential-title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('certificates.mobile-next-credential-text')}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleMobileNextCredential}>
            {t('general.continue')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* PIN entry modal */}
      <Modal show={showModalPin} onHide={hideModalPin} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('documents.kv-pin')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <InputGroup className="mb-3">
            <InputGroup.Prepend>
              <InputGroup.Text id="inputGroup-sizing-default">
                {t('general.pin')}:
              </InputGroup.Text>
            </InputGroup.Prepend>
            <Form.Control
              required
              type="password"
              aria-label="Default"
              aria-describedby="inputGroup-sizing-default"
              autoComplete="new-password"
              value={certificatePin}
              onChange={e => setCertificatePin(e.target.value)}
            />
          </InputGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button className="reject" onClick={hideModalPin}>
            {t('general.cancel')}
          </Button>
          <Button 
            variant="primary" 
            disabled={!certificatePin} 
            onClick={handleAcceptPin}
          >
            {t('general.accept')}
          </Button>
        </Modal.Footer>
      </Modal>

      {error && (
        <Toast
          onClose={() => setError(null)}
          show={!!error}
          animation={true}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            margin: '0 auto',
            backgroundColor: 'rgba(255,255,255,1)',
            border: 'none',
            zIndex: 10
          }}
        >
          <Toast.Header closeButton={true}>
            <strong className="mr-auto">{t('general.error')}</strong>
          </Toast.Header>
          <Toast.Body>{error}</Toast.Body>
        </Toast>
      )}

      {/* Success modal shown after all credentials are socket-confirmed */}
      <Modal
        show={success}
        onHide={() => { onCredentialCreated(); setSuccess(false); }}
        centered
        className="wallet-authorization-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('certificates.wallet-authorization-title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="wallet-auth-modal-body wallet-success-modal-body" style={{ display: 'block' }}>
          <p className="wallet-success-message">
            <strong>{t('certificates.credential-created-linked-success')}</strong>
          </p>
          <div className="wallet-success-image-wrapper">
            <img
              src={successImage}
              alt=""
              className="wallet-success-image"
            />
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CreateVC;
